import React, { useCallback, useState, useEffect } from 'react';
import { Card, Button, Alert, Space, Row, Col, Typography } from 'antd';
import { CameraOutlined, StopOutlined, RetweetOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const MOBILE_BREAKPOINT = 768;

const CameraCaptureTab = ({
  isCapturing,
  isStartingCamera,
  cameraError,
  capturedImages,
  stream,
  videoRef,
  canvasRef,
  onStartCamera,
  onStopCamera,
  onCaptureImage,
  onRemoveImage,
  onPreviewImage
}) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleCaptureImage = useCallback(() => {
    onCaptureImage();
  }, [onCaptureImage]);

  return (
    <Card
      title={isCapturing ? null : 'Capture Images'}
      size="small"
      style={{ marginBottom: 8 }}
      bodyStyle={isCapturing ? { padding: '4px 8px' } : undefined}
    >
      {!isCapturing ? (
        <div style={{ textAlign: 'center', padding: isMobile ? '24px 0' : '40px 0' }}>
          <CameraOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={4}>Ready to Capture</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
            Click the button below to start your camera and capture invoice images
          </Text>
          {cameraError && (
            <Alert
              message="Camera Error"
              description={cameraError}
              type="error"
              showIcon
              style={{ marginBottom: '16px', textAlign: 'left' }}
              action={
                <Button size="small" onClick={() => onStartCamera()}>
                  Dismiss
                </Button>
              }
            />
          )}
          <Space>
            <Button
              type="primary"
              size="large"
              icon={<CameraOutlined />}
              loading={isStartingCamera}
              onClick={onStartCamera}
            >
              {isStartingCamera ? 'Starting Camera...' : 'Start Camera'}
            </Button>
            {cameraError && (
              <Button
                size="large"
                icon={<RetweetOutlined />}
                onClick={onStartCamera}
                loading={isStartingCamera}
              >
                Retry
              </Button>
            )}
          </Space>
        </div>
      ) : (
        <div style={{
          paddingBottom: isMobile ? 80 : 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: isMobile ? 400 : 'auto'
        }}>
          <div
            style={{
              position: 'relative',
              margin: '0 auto',
              maxWidth: isMobile ? '100%' : 500,
              borderRadius: 6,
              overflow: 'hidden',
              border: '1px solid #d9d9d9',
              backgroundColor: '#000',
              flex: 1,
              minHeight: isMobile ? 320 : 400
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: isMobile ? 'calc(100vh - 140px)' : 400,
                minHeight: 320,
                display: 'block',
                objectFit: 'cover',
                verticalAlign: 'top'
              }}
            />
            {/* Document guide overlay - A4 portrait, fills entire area */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              {/* Label overlaid at top - no extra space used */}
              {stream && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: 11,
                    color: '#fff',
                    background: 'rgba(0,0,0,0.5)',
                    padding: '4px 10px',
                    borderRadius: 4,
                    whiteSpace: 'nowrap'
                  }}
                >
                  ✅ Position your invoice inside the frame
                </div>
              )}
              {!stream && isCapturing && (
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: 11,
                    color: '#fff',
                    background: 'rgba(255,77,79,0.8)',
                    padding: '4px 10px',
                    borderRadius: 4
                  }}
                >
                  ⏳ Starting...
                </div>
              )}
              {/* A4 frame - fills overlay height */}
              <div
                style={{
                  height: '100%',
                  width: 'auto',
                  maxWidth: '100%',
                  aspectRatio: '210/297',
                  border: '2px dashed rgba(255, 255, 255, 0.9)',
                  borderRadius: 4,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.15)'
                }}
              />
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>

          {/* Fixed capture bar on mobile - always visible; sticky on desktop */}
          <div
            style={{
              position: isMobile ? 'fixed' : 'relative',
              bottom: isMobile ? 0 : 'auto',
              left: isMobile ? 0 : 'auto',
              right: isMobile ? 0 : 'auto',
              padding: '10px 12px',
              background: isMobile ? 'rgba(255, 255, 255, 0.98)' : 'transparent',
              borderTop: isMobile ? '1px solid #f0f0f0' : 'none',
              marginTop: 4,
              marginBottom: isMobile ? 0 : 0,
              zIndex: 1050,
              boxShadow: isMobile ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <Space size="middle" style={{ width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={<CameraOutlined />}
                onClick={handleCaptureImage}
                size={isMobile ? 'large' : 'large'}
                style={isMobile ? { minWidth: 140 } : {}}
              >
                Capture Image
              </Button>
              <Button
                icon={<StopOutlined />}
                onClick={onStopCamera}
                size={isMobile ? 'large' : 'large'}
                style={isMobile ? { minWidth: 120 } : {}}
              >
                Stop Camera
              </Button>
            </Space>
          </div>
        </div>
      )}

      {capturedImages.length > 0 && (
        <div style={{
          marginTop: 16,
          paddingBottom: isMobile && isCapturing ? 80 : 0
        }}>
          <Text strong>Captured Images ({capturedImages.length}):</Text>
          <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
            {capturedImages.map((image) => (
              <Col span={6} key={image.id}>
                <Card
                  size="small"
                  cover={
                    <img
                      alt="captured"
                      src={image.preview}
                      style={{ height: '80px', objectFit: 'cover' }}
                      onClick={() => onPreviewImage(image)}
                    />
                  }
                  actions={[
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => onPreviewImage(image)}
                      size="small"
                    >
                      Preview
                    </Button>,
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onRemoveImage(image.id)}
                      size="small"
                    >
                      Remove
                    </Button>
                  ]}
                />
              </Col>
            ))}
          </Row>
        </div>
      )}
    </Card>
  );
};

export default CameraCaptureTab;
